import { React } from 'Imports';
import { Box, createTheme, FormControl, ListItemText, MenuItem, MenuProps, Select } from '@mui/material';
import { isEncompass } from '$Utilities/integrationPlatform';
import * as scssStyles from '$CSS/settings.scss';
import DropdownSearchField from './DropdownSearchField';
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';

type Styles = {
    dropdown: string;
    dropdownLabelText: string;
    dropdownLabelTextEnc: string;
    dropdownLabel: string;
    dropdownLabelEnc: string;
    dropdownInput: string;
    dropdownInputEnc: string;
    dropdownSelectedValue: string;
    dropdownSelectedValueEnc: string;
    placeholder: string;
    placeholderEnc: string;
    icon: string;
    iconEnc: string;
    dropdownMenuItem: string;
    dropdownMenuItemEnc: string;
    scrollArea: string;
    scrollAreaEnc: string;
    requiredText: string;
    requiredTextEnc: string;
};

const styles = require('./SingleSelectDropdownMenu.scss') as Styles;

interface ISingleSelectDropdownMenuItem {
    label: string;
    value: string | number;
    onClick: () => void;
}

interface ISingleSelectDropdownMenuProps {
    open: boolean;
    items: ISingleSelectDropdownMenuItem[];
    searchProps:
        | {
              searchable: boolean;
              searchFilter: string;
              onChange: (value: string) => void;
              placeholder?: string;
              hideAdornments?: boolean;
          }
        | undefined;
    onOpen: () => void;
    onClose: () => void;
    label?: string;
    required: boolean;
    defaultValue?: string | number;
    disabled?: boolean;
    sortItems?: boolean;
}

const menuProps: Partial<MenuProps> = {
    transformOrigin: { horizontal: 'left', vertical: 'top' },
    anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
    slotProps: {
        root: {
            sx: {
                width: 'auto',
            },
        },
        paper: {
            sx: {
                overflowY: 'hidden !important',
                maxHeight: 320,
                width: 'auto',
                minWidth: 'max-content',
            },
        },
    },
};

const SingleSelectDropdownMenu = (props: ISingleSelectDropdownMenuProps) => {
    const [dropdownHasFocus, setDropdownHasFocus] = useState(false);

    const [selectedItem, setSelectedItem] = useState<ISingleSelectDropdownMenuItem | undefined>();

    const filteredItems =
        props.searchProps !== undefined && props.searchProps.searchable
            ? props.items.filter((item) => item.label.toLowerCase().includes(props.searchProps?.searchFilter?.toLowerCase() ?? ''))
            : props.items;

    // Sort the filtered items alphabetically by label (sort by default)
    if (props.sortItems == undefined || props.sortItems) {
        filteredItems.sort((a, b) => a.label.localeCompare(b.label));
    }

    const handleOpen = () => {
        props.onOpen();
        setDropdownHasFocus(true);
    };

    const handleClose = () => {
        props.onClose();
        setDropdownHasFocus(false);
    };

    const checkDefaultSelectedItem = (item: ISingleSelectDropdownMenuItem) => {
        if (props.defaultValue !== undefined && selectedItem == undefined && item.value === props.defaultValue) {
            setSelectedItem(item);
            return true;
        }
        return false;
    };

    useEffect(() => {
        const item = filteredItems.find((item) => item.value === props.defaultValue);
        if (item) {
            setSelectedItem(item);
        }
    }, [props.defaultValue]);

    const upArrow = isEncompass ? faCaretUp : faAngleUp;
    const downArrow = isEncompass ? faCaretDown : faAngleDown;

    return (
        <FormControl fullWidth focused={dropdownHasFocus}>
            <Box>
                {props.label && (
                    <Box className={isEncompass ? styles.dropdownLabelEnc : styles.dropdownLabel}>
                        <Box className={isEncompass ? styles.dropdownLabelTextEnc : styles.dropdownLabelText}>
                            {isEncompass ? props.label : props.label + '*'}
                        </Box>
                        {props.required && isEncompass && <Box className={styles.requiredTextEnc}>Required</Box>}
                    </Box>
                )}
            </Box>
            <Select
                disabled={props.disabled}
                sx={{
                    height: '40px',
                    fieldset: {
                        border: dropdownHasFocus
                            ? isEncompass
                                ? '1px solid #0D7AC5 !important'
                                : '1px solid #25477B !important'
                            : isEncompass
                              ? '1px solid #C4CBD4 !important'
                              : '1px solid #C4CBD7 !important',
                    },
                }}
                renderValue={() => (
                    <Box
                        className={
                            isEncompass
                                ? selectedItem
                                    ? styles.dropdownSelectedValueEnc
                                    : styles.placeholderEnc
                                : selectedItem
                                  ? styles.dropdownSelectedValue
                                  : styles.placeholder
                        }
                    >
                        {selectedItem?.label ?? 'Select ...'}
                    </Box>
                )}
                displayEmpty={true}
                autoFocus={false}
                onKeyDown={(e) => e.stopPropagation()}
                inputProps={{
                    className: isEncompass ? styles.dropdownInputEnc : styles.dropdownInput,
                }}
                MenuProps={menuProps}
                onOpen={handleOpen}
                onClose={handleClose}
                open={props.open}
                IconComponent={() => (
                    <Box onClick={props.open ? handleClose : handleOpen} className={isEncompass ? styles.iconEnc : styles.icon}>
                        <FontAwesomeIcon icon={props.open ? upArrow : downArrow} />
                    </Box>
                )}
            >
                {props.searchProps?.searchable && (
                    <DropdownSearchField
                        searchFilter={props.searchProps.searchFilter}
                        onChange={props.searchProps.onChange}
                        onSearchFieldFocus={() => setDropdownHasFocus(false)}
                        onSearchFieldFocusLost={() => setDropdownHasFocus(true)}
                        placeholder={props.searchProps.placeholder}
                        hideAdornments={props.searchProps.hideAdornments}
                    />
                )}
                <Box className={isEncompass ? styles.scrollAreaEnc : styles.scrollArea}>
                    {filteredItems.map((item, index) => (
                        <MenuItem
                            key={index}
                            value={item.value}
                            disableRipple={true}
                            className={isEncompass ? styles.dropdownMenuItemEnc : styles.dropdownMenuItem}
                            selected={checkDefaultSelectedItem(item)}
                            onClick={() => {
                                handleClose();
                                item.onClick();
                                setSelectedItem(item);
                            }}
                        >
                            <ListItemText
                                primary={
                                    props.searchProps?.searchable ? (
                                        <>
                                            {item.label
                                                .split(new RegExp(`(${props.searchProps?.searchFilter})`, 'gi'))
                                                .map((part, index) =>
                                                    part.toLowerCase() === props.searchProps?.searchFilter?.toLowerCase() ? (
                                                        <span key={index} style={{ backgroundColor: '#FFF3B4' }}>
                                                            {part}
                                                        </span>
                                                    ) : (
                                                        part
                                                    ),
                                                )}
                                        </>
                                    ) : (
                                        item.label
                                    )
                                }
                                primaryTypographyProps={{
                                    noWrap: true,
                                    fontSize: isEncompass ? '14px' : '12px',
                                    fontWeight: 400,
                                    textOverflow: 'ellipsis',
                                    color: scssStyles.customColor2,
                                    width: 'max-content',
                                    align: 'right',
                                }}
                                onClick={item.onClick}
                            />
                        </MenuItem>
                    ))}
                </Box>
            </Select>
        </FormControl>
    );
};

export default SingleSelectDropdownMenu;
